// CommissionUpload.jsx — /commission/upload?orderId=xxx (post-purchase)
//                       /commission/preview (staff preview with dummy data)
//
// 4-step guided upload wizard for commission orders. High-friction by design —
// the stakes are high (paid preservation piece), so we want the best possible
// reference image and clear notes from the guest.
//
// Steps: 1. Upload photo  2. Crop/position  3. Notes  4. Review + submit

import { useEffect, useRef, useState } from "react";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import FramingTool from "../FramingTool";
import { normalizePhotoForUpload } from "../utils/photo";

const B = {
  navy:   "#1d52a0",
  blue:   "#1f59db",
  sky:    "#4aa4e1",
  orange: "#f57600",
  gold:   "#fcb31f",
  pink:   "#ef537e",
  cream:  "#ffdaad",
  dark:   "#0d1833",
};

const BG = `radial-gradient(ellipse 140% 95% at 50% 0%,
  #1f59db 0%, #1a4b9e 30%, #0e2e46 70%, #081a2a 100%)`;

const STEPS = ["Upload", "Position", "Details", "Review"];

export default function CommissionUpload({ preview = false }) {
  const [step, setStep] = useState(0);
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Step 1: photo file
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoConverting, setPhotoConverting] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const fileRef = useRef(null);

  // Step 2: framing
  const [croppedBlob, setCroppedBlob] = useState(null);
  const [croppedUrl, setCroppedUrl] = useState(null);
  const [framingState, setFramingState] = useState(null);

  // Step 3: notes
  const [notes, setNotes] = useState("");
  // Piece title — becomes the pancake's public name (subjectName on the
  // linked order). Left blank → the piece gets an "Untitled #N" number.
  const [pieceTitle, setPieceTitle] = useState("");
  const [guestName, setGuestName] = useState("");

  // Load order data (skip in preview mode)
  const orderId = preview ? null : new URLSearchParams(window.location.search).get("orderId");

  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  // The linked order doc in the `orders` collection (where the artist works)
  const [linkedOrderId, setLinkedOrderId] = useState(null);

  useEffect(() => {
    if (preview) {
      setOrderData({ id: "preview", customerName: "Preview Guest", email: "preview@example.com" });
      return;
    }
    if (!orderId) { setError("No order ID found."); setLoading(false); return; }
    getDoc(doc(db, "shopOrders", orderId)).then(snap => {
      if (snap.exists()) {
        const data = snap.data();
        setOrderData({ id: snap.id, ...data });
        setGuestName(data.commissionGuestName || data.customerName || "");
        setLinkedOrderId(data.orderId || null);
        // Check both shopOrders and linked orders doc for prior submission
        if (data.commissionSubmittedAt) {
          setAlreadySubmitted(true);
        } else if (data.orderId) {
          getDoc(doc(db, "orders", data.orderId)).then(oSnap => {
            if (oSnap.exists() && oSnap.data().commissionSubmittedAt) setAlreadySubmitted(true);
          }).catch(() => {});
        }
      } else {
        setError("Order not found.");
      }
      setLoading(false);
    }).catch(err => { setError(err.message); setLoading(false); });
  }, [orderId, preview]);

  // Photo selection. iPhone HEIC photos can't be decoded by the browser's
  // <img>, so normalize (convert HEIC→JPEG client-side + enforce 10MB cap)
  // before handing off to FramingTool's crop step.
  async function handleFileChange(e) {
    const raw = e.target.files?.[0];
    // Clear the input so re-picking the same file after a failure re-fires onChange.
    if (e.target) e.target.value = "";
    if (!raw) return;
    setPhotoError(null);
    setPhotoConverting(true);
    let file;
    try {
      const res = await normalizePhotoForUpload(raw);
      if (res.error) { setPhotoError(res.error); setPhotoConverting(false); return; }
      file = res.file;
    } catch (err) {
      console.warn("photo normalize threw:", err);
      setPhotoError("Couldn't process that photo. Try a different one (JPEG or PNG works best).");
      setPhotoConverting(false);
      return;
    }
    setPhotoConverting(false);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setCroppedBlob(null);
    setCroppedUrl(null);
    setStep(1); // auto-advance to crop
  }

  // Framing callback
  function handleCropped(blob) {
    setCroppedBlob(blob);
    if (croppedUrl) URL.revokeObjectURL(croppedUrl);
    setCroppedUrl(URL.createObjectURL(blob));
  }

  // Submit — writes to both the orders doc (for the artist) and shopOrders (for financial record)
  async function handleSubmit() {
    if (preview || !croppedBlob || !orderId) return;
    setSubmitting(true);
    try {
      const ts = Date.now();
      const path = `commissions/${orderId}/reference_${ts}.jpg`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, croppedBlob, { contentType: "image/jpeg" });
      const imageUrl = await getDownloadURL(ref);

      const framingData = framingState ? {
        framingScale: framingState.scale,
        framingOffsetXFrac: framingState.offsetXFrac,
        framingOffsetYFrac: framingState.offsetYFrac,
        framingDrawnWidthFrac: framingState.drawnWidthFrac,
        framingDrawnHeightFrac: framingState.drawnHeightFrac,
        framingRotation: framingState.rotation || 0,
      } : {};

      // Write to shopOrders (financial record) — only allowed fields
      await updateDoc(doc(db, "shopOrders", orderId), {
        commissionImageUrl: imageUrl,
        commissionNotes: notes.trim() || null,
        commissionGuestName: guestName.trim() || null,
        commissionSubmittedAt: serverTimestamp(),
        ...framingData,
      });

      // Write to linked orders doc (artist workspace) if it exists
      if (linkedOrderId) {
        await updateDoc(doc(db, "orders", linkedOrderId), {
          commissionImageUrl: imageUrl,
          commissionNotes: notes.trim() || null,
          commissionGuestName: guestName.trim() || null,
          commissionSubmittedAt: serverTimestamp(),
          imageUrl: imageUrl,
          recipientName: guestName.trim() || orderData?.customerName || "Commission Guest",
          // Piece title → the pancake's public name; untouched when blank so
          // a staff rename in the Library isn't clobbered by a re-upload.
          ...(pieceTitle.trim() ? { subjectName: pieceTitle.trim() } : {}),
          updatedAt: serverTimestamp(),
          ...framingData,
        }).catch(err => console.warn("Failed to update orders doc:", err.message));
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return (
    <div style={{ minHeight: "100vh", background: BG, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: B.cream, fontSize: "1.1rem" }}>Loading your commission...</div>
    </div>
  );

  if (error && !orderData) return (
    <div style={{ minHeight: "100vh", background: BG, display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
      <div style={{ color: B.pink, fontSize: "1.1rem", textAlign: "center" }}>{error}</div>
    </div>
  );

  if (alreadySubmitted) return (
    <div style={{ minHeight: "100vh", background: BG, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", textAlign: "center", gap: "1rem" }}>
      <div style={{ fontSize: "3rem" }}>✅</div>
      <div style={{ color: B.gold, fontSize: "1.5rem", fontWeight: 900, fontFamily: "var(--jop-display)" }}>
        Reference photo received!
      </div>
      <div style={{ color: B.cream, fontSize: "1rem", lineHeight: 1.6, maxWidth: "400px" }}>
        Your commission is in the queue. Doctor Dan will begin work soon — we'll email you when it's ready.
      </div>
      <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.85rem", marginTop: "0.5rem" }}>
        Need to update your submission? Email us at hello@thejoyofpancakes.com
      </div>
      <button onClick={() => window.location.href = "/"} style={backBtn}>Back to thejoyofpancakes.com</button>
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight: "100vh", background: BG, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", textAlign: "center", gap: "1rem" }}>
      <div style={{ fontSize: "4rem" }}>✨</div>
      <div style={{ color: B.gold, fontSize: "1.8rem", fontWeight: 900, fontFamily: "var(--jop-display)" }}>
        You're all set!
      </div>
      <div style={{ color: B.cream, fontSize: "1.05rem", lineHeight: 1.6, maxWidth: "400px" }}>
        Your reference photo and notes have been submitted. Doctor Dan will begin work on your commission soon — we'll email you when it's ready.
      </div>
      <button onClick={() => window.location.href = "/"} style={backBtn}>Back to thejoyofpancakes.com</button>
    </div>
  );

  return (
    <div style={{
      minHeight: "100vh", background: BG,
      fontFamily: "var(--jop-display), system-ui, sans-serif",
      display: "flex", flexDirection: "column",
      overflow: "hidden", width: "100%", boxSizing: "border-box",
    }}>
      {/* Header */}
      <div style={{
        padding: "1.25rem 1.5rem 0.75rem",
        textAlign: "center",
      }}>
        <div style={{ fontSize: "1.6rem", fontWeight: 900, color: B.gold, letterSpacing: "0.03em" }}>
          🎨 Your Commission
        </div>
        <div style={{ color: B.cream, fontSize: "0.9rem", opacity: 0.7, marginTop: "0.25rem" }}>
          Help us create the perfect pancake for you
        </div>
        {preview && (
          <div style={{
            display: "inline-block", marginTop: "0.5rem",
            background: "rgba(239,83,126,0.2)", border: "1px solid rgba(239,83,126,0.5)",
            borderRadius: "8px", padding: "0.25rem 0.75rem",
            color: B.pink, fontSize: "0.75rem", fontWeight: 700,
          }}>
            STAFF PREVIEW
          </div>
        )}
      </div>

      {/* Step indicator */}
      <div style={{
        display: "flex", justifyContent: "center", gap: "0.4rem",
        padding: "0.5rem 0.75rem 1rem",
        flexWrap: "wrap",
      }}>
        {STEPS.map((label, i) => (
          <div key={label} style={{
            display: "flex", alignItems: "center", gap: "0.35rem",
          }}>
            <div style={{
              width: "28px", height: "28px", borderRadius: "50%",
              background: i <= step ? B.gold : "rgba(255,255,255,0.12)",
              color: i <= step ? B.dark : "rgba(255,255,255,0.4)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.75rem", fontWeight: 900,
              transition: "all 0.3s ease",
            }}>
              {i < step ? "✓" : i + 1}
            </div>
            <span style={{
              color: i <= step ? B.cream : "rgba(255,255,255,0.3)",
              fontSize: "0.75rem", fontWeight: i === step ? 700 : 500,
            }}>
              {label}
            </span>
            {i < STEPS.length - 1 && (
              <div style={{
                width: "20px", height: "2px",
                background: i < step ? B.gold : "rgba(255,255,255,0.12)",
                marginLeft: "0.35rem",
              }} />
            )}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div style={{
        flex: 1, padding: "0 1rem 2rem",
        display: "flex", flexDirection: "column", alignItems: "center",
        width: "100%", boxSizing: "border-box", overflow: "hidden",
      }}>
        {/* ── STEP 0: Upload ── */}
        {step === 0 && (
          <div style={{
            width: "100%", maxWidth: "440px",
            display: "flex", flexDirection: "column", alignItems: "center",
            gap: "1.25rem", boxSizing: "border-box",
            padding: "0 0.5rem",
          }}>
            <div style={{
              background: "rgba(255,218,173,0.06)",
              border: "2px dashed rgba(252,179,31,0.35)",
              borderRadius: "20px",
              padding: "2rem 1.25rem",
              textAlign: "center",
              width: "100%",
              cursor: "pointer",
            }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
              <div style={{ fontSize: "3rem", marginBottom: "0.75rem" }}>📸</div>
              <div style={{
                color: B.gold, fontSize: "1.2rem", fontWeight: 800,
              }}>
                Upload Your Reference Photo
              </div>
              <div style={{
                color: B.cream, opacity: 0.6, fontSize: "0.9rem",
                lineHeight: 1.5, marginTop: "0.5rem",
              }}>
                Tap here to choose a photo from your device
              </div>
            </div>

            {photoConverting && (
              <p style={{ color: B.cream, fontSize: "0.9rem", textAlign: "center", margin: 0, opacity: 0.85 }}>
                ⏳ Getting your photo ready… one sec.
              </p>
            )}
            {photoError && (
              <p style={{ color: B.pink, fontSize: "0.9rem", textAlign: "center", margin: 0, lineHeight: 1.45, fontWeight: 600 }}>
                {photoError}
              </p>
            )}

            <div style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "14px",
              padding: "1rem 1.25rem",
              width: "100%",
            }}>
              <div style={{
                color: B.gold, fontSize: "0.82rem", fontWeight: 700,
                marginBottom: "0.5rem",
              }}>
                Tips for the best result:
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                {[
                  ["💡", "Good lighting — natural light is great, but interesting or playful lighting works too"],
                  ["🎯", "Face centered — give us a clear view of your subject, front-facing works best"],
                  ["📐", "High resolution — the sharper the photo, the sharper the pancake. We may adjust framing for the best result unless you include specific framing notes"],
                  ["😊", "Expression matters — pick the smile, look, or moment you want immortalized in batter"],
                ].map(([icon, text]) => (
                  <div key={text} style={{
                    display: "flex", gap: "0.5rem", alignItems: "flex-start",
                    color: B.cream, fontSize: "0.82rem", lineHeight: 1.4, opacity: 0.75,
                  }}>
                    <span style={{ flexShrink: 0 }}>{icon}</span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 1: Crop/Position ── */}
        {step === 1 && photoFile && (
          <div style={{
            width: "100%", maxWidth: "440px",
            display: "flex", flexDirection: "column", alignItems: "center",
            gap: "1rem",
          }}>
            <div style={{
              color: B.cream, fontSize: "0.95rem", textAlign: "center",
              lineHeight: 1.5, opacity: 0.8,
            }}>
              Pinch to zoom, drag to position. The circle shows the griddle area — center the most important part of the image here.
            </div>

            <FramingTool
              imageFile={photoFile}
              onCropped={handleCropped}
              onStateChange={setFramingState}
            />

            <div style={{ display: "flex", gap: "0.75rem", width: "100%", maxWidth: "360px" }}>
              <button
                onClick={() => setStep(0)}
                style={secondaryBtn}
              >
                ← Back
              </button>
              <button
                onClick={() => { if (photoFile) { fileRef.current?.click(); } }}
                style={secondaryBtn}
              >
                Choose different photo
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!croppedBlob}
                style={{ ...primaryBtn, ...(!croppedBlob ? disabledBtn : {}) }}
              >
                {croppedBlob ? "Looks good →" : "Preparing…"}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Notes ── */}
        {step === 2 && (
          <div style={{
            width: "100%", maxWidth: "440px",
            display: "flex", flexDirection: "column",
            gap: "1rem",
          }}>
            <div style={{
              color: B.cream, fontSize: "0.95rem", textAlign: "center",
              lineHeight: 1.5, opacity: 0.8,
            }}>
              Tell us what matters most about this image. Any details the artist should know?
            </div>

            <div>
              <label style={{ color: B.gold, fontSize: "0.8rem", fontWeight: 700, display: "block", marginBottom: "0.35rem" }}>
                Your name
              </label>
              <input
                value={guestName}
                onChange={e => setGuestName(e.target.value)}
                placeholder="How should we address you?"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ color: B.gold, fontSize: "0.8rem", fontWeight: 700, display: "block", marginBottom: "0.35rem" }}>
                Title of the piece <span style={{ color: B.cream, opacity: 0.5, fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                value={pieceTitle}
                onChange={e => setPieceTitle(e.target.value)}
                placeholder="What is this a picture of?"
                maxLength={60}
                style={inputStyle}
              />
              <div style={{ color: B.cream, opacity: 0.55, fontSize: "0.75rem", marginTop: "0.3rem", lineHeight: 1.4 }}>
                This becomes the piece's name in the gallery. Skip it and we'll number it — like "Untitled #45".
              </div>
            </div>

            <div>
              <label style={{ color: B.gold, fontSize: "0.8rem", fontWeight: 700, display: "block", marginBottom: "0.35rem" }}>
                Notes for the artist
              </label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={"What's important in this photo? Who is this person to you? Any details about features, expression, or context that would help the artist capture the essence.\n\nThe more you tell us, the better the result."}
                rows={5}
                style={{ ...inputStyle, resize: "vertical", minHeight: "120px", lineHeight: 1.5 }}
              />
            </div>

            {croppedUrl && (
              <div style={{
                display: "flex", justifyContent: "center",
              }}>
                <img src={croppedUrl} alt="Your crop" style={{
                  width: "120px", height: "120px", borderRadius: "12px",
                  objectFit: "cover", opacity: 0.7,
                  border: `2px solid rgba(252,179,31,0.3)`,
                }} />
              </div>
            )}

            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button onClick={() => setStep(1)} style={secondaryBtn}>
                ← Back
              </button>
              <button onClick={() => setStep(3)} style={primaryBtn}>
                Review →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Review ── */}
        {step === 3 && (
          <div style={{
            width: "100%", maxWidth: "440px",
            display: "flex", flexDirection: "column",
            gap: "1rem",
          }}>
            <div style={{
              color: B.gold, fontSize: "1.1rem", fontWeight: 800,
              textAlign: "center",
            }}>
              Review your submission
            </div>

            {/* Tappable photo — jumps to upload step */}
            {croppedUrl && (
              <div
                onClick={() => setStep(0)}
                style={{
                  display: "flex", justifyContent: "center", cursor: "pointer",
                  position: "relative",
                }}
              >
                <div style={{
                  borderRadius: "16px", overflow: "hidden",
                  border: `3px solid ${B.gold}`,
                  boxShadow: "0 4px 24px rgba(252,179,31,0.2)",
                  position: "relative",
                }}>
                  <img src={croppedUrl} alt="Your reference" style={{
                    width: "240px", height: "240px", objectFit: "cover", display: "block",
                  }} />
                  <div style={{
                    position: "absolute", bottom: "0.5rem", right: "0.5rem",
                    background: "rgba(0,0,0,0.6)", borderRadius: "8px",
                    padding: "0.2rem 0.5rem",
                    color: B.cream, fontSize: "0.7rem", fontWeight: 600,
                  }}>
                    tap to change
                  </div>
                </div>
              </div>
            )}

            {/* Tappable name — jumps to notes step */}
            <div
              onClick={() => setStep(2)}
              style={{
                background: "rgba(255,255,255,0.05)",
                borderRadius: "10px", padding: "0.7rem 1rem",
                cursor: "pointer",
                border: "1px solid transparent",
                transition: "border-color 0.15s",
              }}
              onPointerEnter={e => e.currentTarget.style.borderColor = "rgba(252,179,31,0.3)"}
              onPointerLeave={e => e.currentTarget.style.borderColor = "transparent"}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.2rem" }}>Name</div>
                <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.65rem" }}>edit</div>
              </div>
              <div style={{ color: B.cream, fontSize: "0.95rem" }}>{guestName.trim() || <span style={{ opacity: 0.4, fontStyle: "italic" }}>No name provided</span>}</div>
            </div>

            {/* Tappable notes — jumps to notes step */}
            <div
              onClick={() => setStep(2)}
              style={{
                background: notes.trim() ? "rgba(255,255,255,0.05)" : "rgba(252,179,31,0.08)",
                borderRadius: "10px", padding: "0.7rem 1rem",
                cursor: "pointer",
                border: notes.trim() ? "1px solid transparent" : `1px solid rgba(252,179,31,0.25)`,
                transition: "border-color 0.15s",
              }}
              onPointerEnter={e => { if (notes.trim()) e.currentTarget.style.borderColor = "rgba(252,179,31,0.3)"; }}
              onPointerLeave={e => { if (notes.trim()) e.currentTarget.style.borderColor = "transparent"; }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.2rem" }}>Notes for the artist</div>
                <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.65rem" }}>edit</div>
              </div>
              {notes.trim() ? (
                <div style={{ color: B.cream, fontSize: "0.95rem", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{notes}</div>
              ) : (
                <div style={{ color: B.gold, fontSize: "0.85rem" }}>
                  Tap to add details that will help the artist
                </div>
              )}
            </div>

            {error && (
              <div style={{ color: B.pink, fontSize: "0.85rem", textAlign: "center" }}>{error}</div>
            )}

            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button onClick={() => setStep(2)} style={secondaryBtn}>
                ← Back
              </button>
              <button
                onClick={preview ? () => alert("Preview mode — submit disabled") : handleSubmit}
                disabled={submitting || (!preview && !croppedBlob)}
                style={{
                  ...primaryBtn,
                  background: "linear-gradient(135deg, #f57600, #fcb31f)",
                  ...((submitting || (!preview && !croppedBlob)) ? disabledBtn : {}),
                }}
              >
                {submitting
                  ? "Submitting..."
                  : (!preview && !croppedBlob)
                    ? "Preparing photo…"
                    : "✨ Submit Commission"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: "1rem", textAlign: "center",
        color: "rgba(255,255,255,0.25)", fontSize: "0.75rem",
      }}>
        thejoyofpancakes.com
      </div>
    </div>
  );
}

// ── BUTTON STYLES ───────────────────────────────────────────────────────────
const primaryBtn = {
  flex: 1,
  background: "#fcb31f",
  color: "#0d1833",
  border: "none",
  borderRadius: "12px",
  padding: "0.85rem 1rem",
  fontSize: "1rem",
  fontWeight: 800,
  cursor: "pointer",
  fontFamily: "var(--jop-display)",
};

// Applied on top of primaryBtn when an action isn't ready yet (e.g. the crop
// blob is still being generated). Communicates "wait" instead of silently no-op.
const disabledBtn = {
  opacity: 0.45,
  cursor: "not-allowed",
  filter: "saturate(0.55)",
};

const secondaryBtn = {
  background: "rgba(255,255,255,0.08)",
  color: "#ffdaad",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: "12px",
  padding: "0.85rem 1rem",
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};

const backBtn = {
  marginTop: "0.5rem",
  background: "rgba(255,255,255,0.1)",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: "999px",
  padding: "0.7rem 1.5rem",
  color: "#ffdaad",
  fontSize: "0.95rem",
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "var(--jop-display)",
};

const inputStyle = {
  width: "100%",
  background: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: "10px",
  color: "white",
  fontSize: "0.95rem",
  padding: "0.7rem 0.9rem",
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
};
